import React from 'react';
import { NavLink } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import type { CourseExtendedCatalogueItem } from '../../types';

import messages from './messages';

import calendarDaysImg from '../../img/calendar-days.svg';

interface CourseInProgressCardProps {
  index: number;
  courseInstance: CourseExtendedCatalogueItem;
  fiveDayCoursesCompletedStatus: Record<string, boolean>;
  fiveDayCoursesList: any[];
  eligibilityStatus: Record<string, boolean>;
  completedStatus: Record<string, boolean>;
  detailsLoaded: Record<string, boolean>;
  enrollmentLink: React.FC;
}

const CourseInProgressCard: React.FC<CourseInProgressCardProps> = ({
  index,
  courseInstance,
  fiveDayCoursesCompletedStatus,
  fiveDayCoursesList,
  eligibilityStatus,
  completedStatus,
  detailsLoaded,
  enrollmentLink,
}) => {
  const intl = useIntl();
  const courseId = courseInstance?.course?.course_id || '';
  const has5DayChallenge = typeof (fiveDayCoursesCompletedStatus?.[
    fiveDayCoursesList.find((pre) => pre?.parentCourseID === courseId && pre?.course_id)?.course_id
  ]) === 'boolean';
  const challenge5DayCourseID = fiveDayCoursesList
    .find((pre) => (pre?.parentCourseID === courseId && pre?.course_id))?.course_id;

  const getAccessLabel = (id: string) => {
    if (eligibilityStatus[id]) {
      return completedStatus[id]
        ? intl.formatMessage(messages.complete)
        : intl.formatMessage(messages.availableNow);
    }
    return intl.formatMessage(messages.completePreviousModule);
  };

  const getFiveDayChallengeAccessLabel = (id: string, fiveDayCourseID: string) => {
    if (eligibilityStatus[id]) {
      return fiveDayCoursesCompletedStatus?.[fiveDayCourseID]
        ? intl.formatMessage(messages.complete)
        : intl.formatMessage(messages.availableNow);
    }
    return intl.formatMessage(messages.completePreviousModule);
  };

  return (
    <div key={`course-item-${courseId}-${index}`} className="course-item">
      {has5DayChallenge && (
        <NavLink
          to={`/course-overview/${challenge5DayCourseID}`}
          className="_5daychallenge-banner"
        >
          <div className="_5daychallenge-banner-details-block">
            <div className="_5daychallenge-banner-header">
              {intl.formatMessage(messages.fiveDay)}
            </div>
            <div className="_5daychallenge-banner-desc">
              {intl.formatMessage(messages.challenge)}
            </div>
          </div>
          <div className="_5daychallenge-icon-block">
            <img
              alt={intl.formatMessage(messages.calendarIconAlt)}
              src={calendarDaysImg}
            />
          </div>
        </NavLink>
      )}
      <div className="top-c">
        <img
          src={`${getConfig().LMS_BASE_URL}${courseInstance?.metadata?.course_image_asset_path}`}
          alt=""
        />
      </div>
      <div className="c-name">{courseInstance?.course?.course_name}</div>
      <p>{courseInstance?.metadata?.description}</p>
      <div className="c-details">
        <span className="it">{intl.formatMessage(messages.timeLabel)}:</span>{' '}
        <span className="reg">{courseInstance?.metadata?.duration}</span>{' '}
        {detailsLoaded[courseId] && (
          <>
            <span className="c_line" />{' '}
            <span className="it">{intl.formatMessage(messages.accessLabel)}:</span>{' '}
            <span id="access" className="reg">
              {getAccessLabel(courseId)}
            </span>
            {enrollmentLink(courseId)}
          </>
        )}
      </div>
      {has5DayChallenge && (
        <div className="c-details _5daychallenge-block">
          <div className="_5daychallenge-icon-block more-m-r">
            <img
              src={calendarDaysImg}
              alt={intl.formatMessage(messages.calendarIconAlt)}
            />
          </div>
          <span className="it">{intl.formatMessage(messages.fiveDayChallenge)}</span>{' '}
          <span className="c_line" />
          <span id="access" className="reg">
            {getFiveDayChallengeAccessLabel(courseId, challenge5DayCourseID)}
          </span>
          {enrollmentLink(challenge5DayCourseID)}
        </div>
      )}
    </div>
  );
};

export default CourseInProgressCard;
